export default {
  navbar: {
    logo: {
      src: `/logo.svg`,
    },
    navbarLinks: {
      links: [`home`],
      home: {
        text: `Home`,
        url: `/`,
      },
      subscription: {
        text: `Subscription`,
        url: `/subscription`,
      },
      support: {
        text: `Support`,
        url: `/support`,
      },
      aboutUs: {
        text: `about us`,
        url: `/about`,
      },
    },
    navbarButtons: [
      {
        text: `Subscription`,
        endpoint: '/subscription'
      },
      {
        text: `Teachers`,
        endpoint: '/teachers'
      },
      {
        text: `Support`,
        endpoint: '/support'
      },
      {
        text: `Teach on Tatva`,
        endpoint: '/teach-on-tatva'
      },
    ],
  },

  homepage: {
    sections: [
      `hero`,
      `section1`,
      `section2`,
      `testimonials`,
      `section3`,
      `statistics`,
      `section4`,
    ],

    hero: {
      video: {
        url: `/homepage/hero-background.webm`,
      },
      title: {
        text: `Experience Yoga's True Essence`
      },
      subTitle: {
        text: `Expert Guidance and Personalised Attention`,
      },
    },

    aboutSection: {
      title: "Authentic Yoga, Guided by *Real Teachers*",
      subtitle: "Every Tatva session is live, personal, and led by a certified instructor — not a video, not an algorithm. Real feedback, real progress.",
      images: [
        "/homepage/renata_1.webp",
        "/homepage/katarina_1.webp",
        "/homepage/YogiAshokananda_image2.webp",
        "/homepage/francesca_2.webp",
        "/homepage/joao_1.webp",
      ],
      stats: [
        {
          value: 54,
          label: "Countries",
          description: "Practitioners tuning in from every time zone, united by one practice."
        },
        {
          value: 48000,
          label: "Participants",
          description: "A global community showing up for themselves, one class at a time."
        },
        {
          value: 6000,
          label: "Classes",
          description: "Live sessions across every style and level, with new classes added weekly."
        }
      ],
      buttonText: "join your first class free with us"
    },


    coreProblems: {
      title: "We Know Why It's Hard To\n*Show Up*",
      subtitle:
        "Finding time, the right teacher, or even the confidence to begin can feel harder than the practice itself. Tatva removes the everyday barriers between you and your practice.",

      ctaText: "Overcome All Obstacles With Tatva",

      problems: [
        {
          title: "Your life doesn't follow a fixed schedule",

          description:
            "Some days start at 6 AM. Some end at midnight. When yoga only happens on the studio's schedule, it's easy to keep saying, 'I'll go tomorrow.' Tatva brings the practice to your schedule — with live and on-demand classes you can join when it works for you.",

          icon: "FiClock",
          image: "/homepage/problem1.webp"
        },

        {
          title: "The right teacher shouldn't feel out of reach",

          description:
            "Finding a teacher you truly connect with can mean expensive memberships, long commutes, and limited choices. Tatva brings experienced teachers from around the world closer to you — so you can learn from someone who understands your practice, without leaving home.",

          icon: "FiGlobe",
          image: "/homepage/YogiAshokananda_image.webp"
        },

        {
          title: "You shouldn't have to feel watched to feel welcome",

          description:
            "Maybe you're new to yoga. Maybe you're still finding your balance. Practicing around strangers can make every pose feel like a performance. On Tatva, your space is your own. Pause, repeat, make mistakes, and grow at a pace that feels comfortable to you.",

          icon: "FiShield",
          image: "/homepage/problem3.webp"
        },

        {
          title: "Your practice is personal. Your class should be too.",

          description:
            "Your body, experience, goals, and energy aren't the same every day — so one generic class can't possibly fit everyone. Whether you're starting out, building strength, improving flexibility, or going deeper, Tatva helps you find a practice that feels right for you.",

          icon: "FiTrendingUp",
          image: "/homepage/problem4.webp"
        },

        {
          title: "It's easier to stay consistent when you're not doing it alone",

          description:
            "Motivation feels great on day one. It's consistency that changes you. When nobody is expecting you, skipping one session can easily become a week. Live classes, a welcoming community, and gentle reminders give you the encouragement to keep coming back.",

          icon: "FiUsers",
          image: "/homepage/problem5.webp"
        },

        {
          title: "Stop searching. Start practicing.",

          description:
            "You've probably saved dozens of yoga videos, only to spend more time searching than practicing. Tatva brings guided classes, recorded sessions, and community practices together in one place — available whenever you're ready to step onto the mat.",

          icon: "FiPlayCircle",
          image: "/homepage/Problem6.webp"
        }
      ]
    },

    credibilitySection: {
      tag: "LIVE INTERACTIVE CLASSES",
      title: "Practice Together Live Online *Anywhere*",
      subtitle: "Feel the energy of a studio class without leaving home. Join live sessions, get real-time adjustements and guidance from your teacher.",
      ctaText: "Join A Live Class Today",
      images: [
        "/homepage/authentic_yoga_1.webp",
        "/homepage/authentic_yoga_2.webp",
        "/homepage/authentic_yoga_3.webp",
        "/homepage/authentic_yoga_4.webp",
      ]
    },

    videoSection: {
      tag: "ON-DEMAND VIDEOS",
      title: "Practice On Your *Time*",
      subtitle: "Can't make a live class? Press play on a full library of guided practices taught by real teachers, whenever you're ready to step onto the mat.",
      video: { url: "/homepage/VideoDescription.webm" },
      points: [
        {
          icon: "FiPlayCircle",
          title: "A library of guided classes",
          description: "Asana, mindfulness, fitness and movement, prana and kriya, and master classes, all in one place.",
        },
        {
          icon: "FiRotateCcw",
          title: "Pick up where you left off",
          description: "Your progress is saved on every video, so you can resume exactly where you paused.",
        },
        {
          icon: "FiFilter",
          title: "Find the right practice fast",
          description: "Filter by language and duration, or search by teacher and style.",
        },
        {
          icon: "FiHeart",
          title: "Like and share what moves you",
          description: "Save your favourites with a like and send a video to a friend in one tap.",
        },
        {
          icon: "FiUnlock",
          title: "Free to start",
          description: "Begin with free community videos, then unlock your favourite teachers' full libraries.",
        },
      ],
      ctaText: "Browse The Video Library",
    },

    testimonials: {
      tag: `WHAT OUR COMMUNITY SAYS`,
      title: `Reflections From Our *Community*`,
      subtitle: `Real students, real progress — hear how a consistent practice with Tatva changed the way they move, breathe, and show up for themselves.`,
      stats: {
        value: `50K+`,
        description: `practitioners already building a stronger, calmer life with Tatva. Yours starts here.`
      },
      ctaText: `Start Your Practice`,
      listOfTestimonials: [
        {
          comment: `I never thought I'd stick with a home practice, but the live classes changed that. My teacher notices when my alignment is off, through a screen, and that personal attention keeps me coming back every morning.`,
          authorName: `Stephanie Worse`,
          authorDesignation: `Practicing Vinyasa since 2024`,
          authorAvatar: `/homepage/catherin.webp`,
          background: `/homepage/pregnant-woman-yoga-sitting-portrait.webp`,
        },
        {
          comment: `Between work and family, studio classes never fit my schedule. Now I roll out my mat at 6 AM before anyone's awake, and I've never felt stronger, physically or mentally.`,
          authorName: `Marcus Thorne`,
          authorDesignation: `Early-morning Hatha practitioner`,
          authorAvatar: `/homepage/zoe.webp`,
          background: `/homepage/woman-yoga-sitting-portrait.webp`,
        },
        {
          comment: `I was the most self-conscious beginner in every class I tried. Practicing from home let me learn at my own pace, and this community made me feel like I belonged from day one.`,
          authorName: `Elena Rodriguez`,
          authorDesignation: `New to yoga, one year in`,
          authorAvatar: `/homepage/dillarosalim.webp`,
          background: `/homepage/woman-yoga-standing-portrait.webp`,
        },
        {
          comment: `What keeps me on my mat is the consistency: live sessions, real feedback, and teachers who remember my name and my old knee injury. That's not something an app alone can give you.`,
          authorName: `David Lin`,
          authorDesignation: `Practicing through recovery, 8 months strong`,
          authorAvatar: `/homepage/comment-avatar-1.webp`,
          background: `/homepage/woman-yoga-sitting-landscape.webp`,
        }
      ],
    },

    mastersDetails: {
      tag: "MEET YOUR TEACHERS",
      title: "Learn From Teachers Who *Live The Practice*",
      subtitle: "These aren't fitness coaches or influencers. They're practitioners — people who have spent years on the mat and want to bring that to you.",
      ctaText: "Explore All Teachers",
      ctaUrl: "/teachers",
      teachers: [
        {
          name: "Yogi Ashokananda",
          joinedYear: "Teaching since 1990",
          designation: "35+ years of practice. A true lineage teacher rooted in classical Indian yoga and meditation.",
          imageUrl: "/homepage/yogi-ashokananda-left.webp",
          teacherInfoUrl: "https://tatva.app/teacher?id=yogi",
        },
        {
          name: "Realieveness",
          joinedYear: "Teaching since 2020",
          designation: "Certified RYT-500 in Himalayan Hatha and Prana Kriya — precise alignment, breath-led practice.",
          imageUrl: "/homepage/renata_1.webp",
          teacherInfoUrl: "https://tatva.app/teacher?id=real",
        },

        {
          name: "Katarina Kamara",
          joinedYear: "Teaching since 2017",
          designation: "Indian-tradition yoga with a modern lens — focused, meditative, deeply restorative.",
          imageUrl: "/homepage/katarina_1.webp",
          teacherInfoUrl: "https://tatva.app/teacher?id=kata",
        },
        {
          name: "Francesca",
          joinedYear: "Teaching since 1999",
          designation: "Inclusive yoga for all — adults, children, and practitioners with special needs.",
          imageUrl: "/homepage/francesca_2.webp",
          teacherInfoUrl: "https://tatva.app/teacher?id=fran",
        },
        {
          name: "João Serejo",
          joinedYear: "Teaching since 2004",
          designation: "Slow-down specialist. Yin, Restorative, and Yoga Nidra for recovery and deep rest.",
          imageUrl: "/homepage/joao_1.webp",
          teacherInfoUrl: "https://tatva.app/teacher?id=joos",
        },
        {
          name: "Beatrice",
          joinedYear: "Teaching since 2001",
          designation: "15+ years of teaching experience. A deeply calming presence specializing in Meditation and Yoga Nidra.",
          imageUrl: "/homepage/Beatrice_image.webp",
          teacherInfoUrl: "https://web.tatva.app/teacher?id=beat",
        },
        {
          name: "Carlo Mancini",
          joinedYear: "Teaching since 2002",
          designation: "Postural yoga for resilience and recovery. Dedicated to helping you build physical acceptance and lasting inner peace.",
          imageUrl: "/homepage/CarloMankini_image.webp",
          teacherInfoUrl: "https://web.tatva.app/teacher?id=carl",
        }
      ],
      forTeachers: {
        title: "Are you a master teacher?",
        subtitle: "Bring your practice to a global audience. We provide the platform so you can focus on what you do best—teaching.",
        features: [
          {
            title: "Two-Way Video",
            description: "See your students in high-definition to provide real-time, personalized adjustments.",
            icon: "FiVideo"
          },
          {
            title: "Global Reach",
            description: "Connect with dedicated students from all over the world, right from your own space.",
            icon: "FiGlobe"
          },
          {
            title: "Zero Tech Hassles",
            description: "No complicated streaming setups. Just open your laptop, go live, and teach.",
            icon: "FiShield"
          }
        ],
        ctaText: "Teach on Tatva"
      }
    },


  },
  footer: {
    title: `Experience Yoga's True Essence: Expert Guidance and Personalized Attention from Anywhere`,
    siteLinks: [
      { text: `Teach With Us`, url: `/teach-on-tatva` },
      { text: `Tatva at Work`, url: `/` },
      { text: `Contact Information`, url: `/` },
      { text: `Tatva at Home`, url: `/` },
      { text: `Address`, url: `/` },
      { text: `Tatva on the Go`, url: `/` },
    ],
    getAppContainer: {
      title: `Get the app`,
    },
  },
  finalSection: {
    title: "Your Mat Is Ready. *So Is Your Teacher.*",
    subtitle: "Every class on Tatva is led by a real teacher who notices when you're struggling, celebrates when you grow, and remembers your name long after the first session — not a video, not an algorithm. Just someone who shows up for you, the way you're showing up for yourself.",
    ctaText: "Get Started Free",
    address: "Tatva Studio\n27 Lucas Avenue, Harrow HA2 9UH (United Kingdom)",
    phone: "+447900343135",
    email: "support@tatva.app",
    facebook: "Tatva Wellness",
    instagram: "@tatva_wellness",
    youtube: "Tatva Wellness",
    slogan: "Personalised and Authentic Yoga",
    bottomLogoText: "TATVA",
    copyright: "©2026 Tatva Wellness Pvt Ltd"
  },
  supportpage: {
    FAQSection: {
      title: `Frequently asked *questions.*`,
      categories: [
        {
          id: "users",
          tag: "PRACTITIONERS & STUDENTS",
          label: "Practitioner’s Guide",
          cta: { text: "Get Started", url: "https://web.tatva.app/" },
          title: "Everything for *Practitioners*",
          subtitle: "Discover how to navigate classes, levels, schedule sessions, and begin your yoga journey.",
          image: "/homepage/PractitionerSupport.webp",
          imageAlt: "Tatva Yoga Practitioner",
          faqs: [
            {
              title: "What is the Tatva App?",
              content: "Tatva is a digital yoga sanctuary dedicated to making authentic, lineage-based yogic practices available worldwide. With expert teachers, live online classes, and an on-demand video library, transformative practice is accessible anywhere."
            },
            {
              title: "What types of classes are available?",
              content: "Tatva offers Yoga & Asana, Prana & Kriya, Meditation & Mindfulness, and one-of-a-kind Master Classes — available both as live interactive sessions and on-demand recordings in our Yogashala library."
            },
            {
              title: "Are classes suitable for my experience level?",
              content: "Yes! Every class listing includes level guidance (Beginner, Intermediate, Advanced, or All Levels) along with detailed practice descriptions so you can choose sessions that align with your current journey."
            },
            {
              title: "Where can I find the live class schedule?",
              content: "You can view the upcoming live class schedule directly on the Home dashboard of the Tatva app or website. Filter by date, teacher, or style to reserve your spot."
            },
            {
              title: "How do I join a live online class?",
              content: "To join a live class, open the Tatva app 15 minutes before the scheduled start time. Live classes stream interactively via Zoom — simply tap 'Join Class'. If scanning a QR code, ensure camera permissions are enabled."
            },
            {
              title: "Can I access free community classes?",
              content: "Absolutely! Free accounts (Blue tier) include access to all free community classes. You can upgrade anytime to access full live schedules and the entire Yogashala video library."
            }
          ]
        },
        {
          id: "teachers",
          tag: "TEACHERS & GUIDES",
          label: "Teacher’s Guides",
          cta: { text: "Apply to Teach", url: "https://teachers.tatva.app" },
          title: "Everything for *Teachers*",
          subtitle: "Learn how to host live sessions, manage batches, publish video series, and automate payouts.",
          image: "/homepage/teachersGuide.webp",
          imageAlt: "Tatva Teacher Workspace",
          faqs: [
            {
              title: "How do I apply to become a teacher on Tatva?",
              content: "You can sign up directly on the Tatva Teachers Workspace. Simply create an account, complete your profile with your yoga lineage and credentials, and start scheduling classes."
            },
            {
              title: "How do live online classes work for teachers?",
              content: "Tatva integrates directly with Zoom. When you create a class on your teacher dashboard, Zoom links and student access codes are generated automatically — no manual emailing required."
            },
            {
              title: "Can I upload pre-recorded video series?",
              content: "Yes! Teachers can publish on-demand classes and structured video courses to the Yogashala library, allowing students worldwide to practice with you anytime."
            },
            {
              title: "How do payouts and earnings work?",
              content: "Tatva automates earnings payouts directly to your linked bank account. You set your custom plan pricing, class pass rates, or subscription fees and keep full visibility via your revenue analytics dashboard."
            },
            {
              title: "Can I manage student groups and attendance?",
              content: "Yes, your teacher dashboard provides real-time attendance rosters, student enrollment lists, and communication tools so you can engage your community effortlessly."
            },
            {
              title: "What are the costs to join as a teacher?",
              content: "Creating a teacher workspace is free to set up with zero upfront costs or lock-in contracts. You can bring existing student batches and start teaching live immediately."
            }
          ]
        },
        {
          id: "subscriptions",
          tag: "SUBSCRIPTIONS & BILLING",
          label: "Subscriptions & Billing",
          title: "Plans, Payments & *Subscriptions*",
          subtitle: "Understand plan tiers, cancellation policies, payment options, and membership benefits.",
          image: "/homepage/BillingsSupport.webp",
          imageAlt: "Tatva Membership & Plans",
          faqs: [
            {
              title: "What subscription plans are available?",
              content: "Tatva offers flexible tiers: Blue (Free community access), Bronze (£7.99/mo for Yogashala videos), Teacher Add-ons, Class Pass Packs (£24 per 4 classes), and Platinum (£120/mo for unlimited live & video access)."
            },
            {
              title: "What is the difference between a free and paid subscription?",
              content: "Free (Blue) accounts provide access to community classes. Paid subscriptions (Bronze, Platinum, or Teacher Add-ons) unlock complete access to live interactive classes, full Yogashala archives, and exclusive member gifts."
            },
            {
              title: "How do I upgrade or change my plan?",
              content: "You can upgrade or switch your plan anytime. Open the main menu, go to 'My Subscription', select your desired tier, and your new access takes effect immediately."
            },
            {
              title: "Can I cancel my subscription anytime?",
              content: "Yes! There are no lock-in contracts. You can cancel anytime under 'My Subscription'. You will retain full access until the end of your current billing period with no further charges."
            },
            {
              title: "Will I be charged automatically upon renewal?",
              content: "Yes, subscriptions auto-renew at the end of each billing period. We send an email reminder before each renewal, and you can manage auto-renew settings in your account."
            },
            {
              title: "How do promotional discount codes work?",
              content: "Enter your discount code (e.g. TATVA50) during checkout on the Pricing page or app to claim welcome offers or promotional discounts."
            }
          ]
        },
        {
          id: "technical",
          tag: "TECHNICAL & APP SUPPORT",
          label: "Technical & Support",
          title: "Technical & *App Support*",
          subtitle: "Get quick troubleshooting for Zoom streaming, camera permissions, account access, and devices.",
          image: "/homepage/technical-support.webp",
          imageAlt: "Tatva Technical Support",
          faqs: [
            {
              title: "Why isn't my Zoom live class loading?",
              content: "Ensure you have the latest Zoom app installed on your device. Try closing and reopening the Tatva app, or click the direct Zoom link provided on your class confirmation screen."
            },
            {
              title: "How do I enable camera & microphone access?",
              content: "Go to your device's Settings > Privacy > Camera & Microphone, and ensure access is granted for the Tatva app. This enables seamless live class participation."
            },
            {
              title: "Can I use Tatva on multiple devices?",
              content: "Yes! Your Tatva account works across all your devices — smartphones, tablets, and web browsers. Simply log in with your account credentials."
            },
            {
              title: "What should I do if a video buffers or doesn't play?",
              content: "Check your internet connection speed. If the issue persists, try lowering the video resolution in the player settings, clear your browser cache, or restart the app."
            },
            {
              title: "How do I reset my account password?",
              content: "Tap 'Forgot Password' on the login screen and enter your registered email address. You will receive a password reset link within a few moments."
            },
            {
              title: "What is the recommended app version?",
              content: "We recommend keeping your Tatva app updated to version 2.0.19 or higher to ensure optimal performance, enhanced security, and access to all new features."
            }
          ]
        }
      ]
    },
    getInTouchSection: {
      title: `Get in Touch`,
      subtitle: `Have Questions or Ready to Begin? Reach Out to Us - We're Here to Help`,
      form: {
        name: {
          label: `Name`,
        },
        phone: {
          label: `Phone Number`,
        },
        dropdown: {
          adornment: `You are a`,
          options: [
            { text: `teacher`, value: `teacher` },
            { text: `student`, value: `student` },
          ],
        },
        typeName: {
          label: `SUBJECT`,
          placeholder: `e.g. Payment issue`,
        },
        message: {
          label: `Message`,
        },
        button: {
          text: `SEND`,
        },
      },
      contacts: {
        phone: {
          label: `WHATSAPP`,
          value: `Click to Chat`,
        },
        email: {
          label: `EMAIL`,
          value: `support@tatva.app`,
        },
      },
    },
    slaSection: {
      title: `**We're Here When You Need Us**`,

      description:
        `Questions, payments, classes, or something not working as expected? You don't have to figure it out alone. Our support team is here to listen, guide you, and help you get back to your practice with as little friction as possible.`,

      subtitle1: `When will I hear back?`,

      subDescription1:
        `We aim to respond to every support request within **48 hours** during our support hours, **8:00 AM to 8:00 PM UK/London Time (BST)**. Once you reach out, we'll make sure your request is acknowledged and guide you toward the right solution.`,

      subtitle2: `Talk to someone from Tatva`,

      subDescription2:
        `Sometimes a quick conversation is all you need. Choose the support channel that feels most convenient for you — we're happy to help.`,

      point1:
        `**Email Support:** Reach us at **teacher-support@tatva.app** for questions, account concerns, payments, classes, or anything else you need help with.`,

      point2:
        `**Phone Support:** Prefer to speak with someone? Call **+447900343135**, Monday to Friday, **8:00 AM to 8:00 PM UK/London Time (BST)**.`,

      subtitle3: `Need help right now?`,

      subDescription3:
        `For a more personal, real-time conversation, connect with our support team on WhatsApp. Send us a message and we'll help you work through it.`,

      point3:
        `**WhatsApp Chat:** Message our support team at **+916203310325** for direct assistance.`,

      footer:
        `Your experience matters to us. Whatever you're stuck on, reach out — we'll do our best to make the next step easier.`,
    },

    hero: {
      tag: `TATVA SUPPORT`,

      title: `Stuck on something? \n*Let's figure it out together.*`,

      subtitle:
        `Whether you have a question about your membership, can't join a class, or simply need a little guidance, you're in the right place. Find an answer below or reach out to someone from the Tatva team.`,

      searchPlaceholder:
        `What can we help you find? `,

      popularSearches: [
        { label: `How do I join a live class?`, categoryId: `users` },
        { label: `How do payouts work?`, categoryId: `teachers` },
        { label: `How do I cancel my subscription?`, categoryId: `subscriptions` },
        { label: `How do I reset my password?`, categoryId: `technical` },
      ],
    },
    contact: {
      title: "Get in *touch.*",
      subtitle: "Can't find what you're looking for? Send a message or reach us directly — we're always close by.",
      leftPane: {
        title: "Send us a *message.*",
        subtitle: "Fill in the form and our team will respond during operating hours — usually within a few hours."
      },
      rightPane: {
        title: "Reach us *directly.*",
        subtitle: "Prefer to talk or message? Here are all the ways to get hold of our team.",
        options: [
          {
            icon: "whatsapp",
            type: "REAL-TIME SUPPORT",
            title: "WhatsApp chat",
            text: "Connect with an agent instantly during operating hours.",
            linkText: "Start chat →",
            url: "https://wa.me/916203310325"
          },
          {
            icon: "email",
            type: "EMAIL SUPPORT",
            title: "support@tatva.app",
            text: "For detailed or account-related enquiries.",
            linkText: "Send email →",
            url: "mailto:support@tatva.app"
          },
          {
            icon: "phone",
            type: "PHONE SUPPORT",
            title: "+44 7404644039",
            text: "Call us during operating hours.",
            linkText: null,
            url: "tel:+447404644039"
          }
        ],
        hours: {
          title: "Operating hours",
          text1: "Monday – Friday · 8 AM to 8 PM · UK/London time (BST)",
          text2: "We aim to respond to all enquiries as early as possible."
        }
      }
    },
    atAGlance: {
      title: "Support *at a glance.*",
      subtitle: "Everything you need to know about how we support you.",
      cards: [
        {
          tag: "RESPONSE SLA",
          title: "48 hours",
          subtitle: "First response time",
          text: "We reply to every enquiry within 48 hours of receiving it during operating hours."
        },
        {
          tag: "AVAILABILITY",
          title: "8 AM – 8 PM",
          subtitle: "Monday to Friday, BST",
          text: "Our support team is available every weekday to help with any questions or concerns."
        },
        {
          tag: "CURRENT RELEASE",
          title: "V 2.0.19",
          subtitle: "Latest app version",
          text: "Keep your app updated to access the latest features and performance improvements."
        }
      ]
    },
    testimonial: {
      quote: `"Every question is a step closer to your practice. We're honoured to be part of that journey."`,
      author: `— THE TATVA SUPPORT TEAM`
    },
    footerCTA: {
      badge: "🌸 HERE WHEN YOU NEED US",

      title: "You don't have to figure it out *alone.*",

      subtitle:
        "From finding the right class to understanding your membership or solving a technical issue, we're here to make things easier. Reach out to us , we are here to help.",

      primaryButton: "Chat with Us on WhatsApp →",

      primaryUrl: "https://wa.me/916203310325",

      secondaryButton: "Get Help by Email →",

      secondaryUrl: "mailto:support@tatva.app",

      benefits: [
        "We're here whenever you need us",
        "Personal support from the Tatva team",
        "Free plan always available"
      ],

      note:
        "Support Hours: Mon–Fri · 8 AM – 8 PM BST · App version 2.0.19"
    },
  },
  aboutpage: {
    sections: [`section1`, `section2`, `section3`],
    section1: {
      image: {
        url_desktop: `/aboutpage/about-page-yoga.png`,
        url_mobile: `/aboutpage/about-page-mobile.png`,
      },
      title: {
        text: `Our Vision`,
      },
      content: {
        desc1: `Our Purpose is to help Tatva participants to unlock the power of time-tested, holistic practices to redefine how we live - from mere doing to pure being.`,
        desc2: `Our purpose is to be more than just a yoga platform; we aspire to become a trailblazing Yoga and Technology company. Leveraging the fusion of ancient wisdom and cutting-edge innovations, we will lead the way in revolutionizing the yoga experience.`,
      },
    },
    section2: {
      title: {
        text: `The Benefits of Yoga with Tatva`,
      },
      list: [
        {
          listTitle: `Authenticity`,
          listDesc: `Authenticity is a vital aspect of Tatva’s offering. While practicing yoga live online, it is essential to prioritize authenticity in your practice. Our teachers embody the core principles and values of yoga, fostering a genuine and holistic approach.`,
        },
        {
          listTitle: `Personalized`,
          listDesc: `We prioritize personalized attention in our live online yoga classes. Our teachers take the time to understand your unique needs, limitations, and goals, offering modifications, adjustments, and individualized guidance to ensure a safe and effective practice.`,
        },
        {
          listTitle: `Practice Anywhere`,
          listDesc: `Tatva allows you to practice from the comfort of your own home or any other location with internet access. You do not have to commute to a studio or adhere to specific class schedules. This flexibility enables you to fit yoga into your routine more easily.`,
        },
      ],
    },
    section3: {
      title: {
        text: `Meet the Team`,
      },
      list: [
        {
          image: {
            url: `/aboutpage/default-profile.png`,
          },
          name: `Anurag Jain`,
          desc: `Lorem Ipsum`,
        },
        {
          image: {
            url: `/aboutpage/default-profile.png`,
          },
          name: `Yogi Ashokananda`,
          desc: `Lorem Ipsum`,
        },
        {
          image: {
            url: `/aboutpage/default-profile.png`,
          },
          name: `Michele Carvalho`,
          desc: `Lorem Ipsum`,
        },
      ],
    },
    popUp: {
      Authenticity: {
        title: `Authenticity`,
        background: `/aboutpage/pregnant-woman-exercise.png`,
        mobile_background: `/aboutpage/mobile-exercise.png`,
        desc: [
          {
            title: `Mindful Teaching`,
            content: `Tatva’s teachers understand that yoga is a deeply personal practice and encourage students to honor their unique bodies, abilities, and limitations. You will receive modifications and options, empowering you to adapt the practice to your needs while maintaining safety and integrity.`,
          },
          {
            title: `Respect for Individuality`,
            content: `Tatva’s teachers understand that yoga is a deeply personal practice and encourage students to honor their unique bodies, abilities, and limitations. You will receive modifications and options, empowering you to adapt the practice to your needs while maintaining safety and integrity.`,
          },
          {
            title: `Alignment with Tradition`,
            content: `Tatva’s teachers understand that yoga is a deeply personal practice and encourage students to honor their unique bodies, abilities, and limitations. You will receive modifications and options, empowering you to adapt the practice to your needs while maintaining safety and integrity.`,
          },
        ],
      },
      Personalized: {
        title: `Personalized`,
        background: `/aboutpage/pregnant-woman-yoga.png`,
        mobile_background: `/aboutpage/mobile-exercise.png`,
        desc: [
          {
            title: `Variety of Classes`,
            content: `Tatva provides a wide range of yoga classes, catering to different styles, levels, durations, and intentions. You can explore different teachers and styles to find what resonates with you the most. This variety helps keep your practice fresh and prevents monotony.`,
          },
          {
            title: `Flexibility and Progression`,
            content: `Tatva allows you to practice at your own pace and choose the classes that suit your needs. You can also track your progress over time, access past classes, and revisit specific practices.`,
          },
          {
            title: `Access to Expertise`,
            content: `Tatva features experienced and knowledgeable teachers from around the world. You can access their expertise and guidance regardless of your geographical location. It is an opportunity to learn from renowned teachers who might not be available locally.`,
          },
        ],
      },
      "Practice Anywhere": {
        title: `Practice Anywhere`,
        background: `/aboutpage/pregnant-woman-yoga.png`,
        mobile_background: `/aboutpage/mobile-exercise.png`,
        desc: [
          {
            title: `Privacy and Comfort`,
            content: `Practicing at your preferred location gives you the freedom to create a space that feels comfortable and safe. You can wear what you prefer, adjust the temperature, and practice without feeling self-conscious. This privacy can deepen your focus and relaxation during yoga sessions.`,
          },
          {
            title: `Convenience`,
            content: `Tatva allows you to practice from the comfort of your own home or any other location with internet access. You do not have to commute to a studio or adhere to specific class schedules. This flexibility enables you to fit yoga into your routine more easily.`,
          },
          {
            title: `Cost-Effectiveness`,
            content: `Live Online yoga classes are more affordable than in-person studio sessions. We offer subscription-based options or individual class purchases that fit within your budget. This accessibility makes yoga more financially sustainable for many people.`,
          },
        ],
      },
    },
  },
  pricepage: {
    hero: {

      title: "Your practice deserves <br/> a *place to grow.*",

      subtitle:
        "Yoga is easier to stay committed to when it fits into your life. Practice when you have time, learn from teachers you trust, and keep coming back to a space that grows with you — from your first class to a deeper, lifelong practice.",

      stats: [
        { value: "40+", label: "EXPERIENCED TEACHERS" },
        { value: "2k+", label: "CLASSES TO EXPLORE" },
        { value: "98%", label: "MEMBERS WHO LOVE TATVA" },
      ],

      benefitsTitle: "More than a membership.<br/> *A practice you can keep.*",

      benefitsSubtitle:
        "The hardest part isn't knowing that yoga is good for you. It's making space for it, staying consistent, and finding the right guidance. Tatva makes every part of that journey easier.",

      benefits: [
        {
          title: "Practice on your terms",
          desc:
            "Morning, evening, 10 minutes or an hour — choose live classes or on-demand practices that fit naturally into your day.",
        },
        {
          title: "Feel guided, not alone",
          desc:
            "Learn from experienced teachers who help you understand your body, build confidence, and practice with purpose.",
        },
        {
          title: "Find your place to belong",
          desc:
            "Practice alongside people who are on their own journeys too. Share the energy of a community that keeps you coming back.",
        },
        {
          title: "See how far you've come",
          desc:
            "Build consistency, celebrate milestones, and turn small practices into progress you can actually feel.",
        },
      ],
    },
    testimonial: {
      quote: `"Tatva gave me something I had been missing for years — a practice I actually look forward to. It feels less like a subscription and more like a place I can return to, whenever I need it."`,

      author: `— MEERA S., TATVA MEMBER SINCE 2023`,
    },

    plansHeader: {
      title: `Choose the plan that <br/> *fits your practice.*`,

      subtitle:
        `Whether you're building a new habit or deepening a practice you've already made your own, choose the freedom, guidance, and consistency you need.`,
    },
    monthlyHeader: {
      title: `Monthly subscription`,
      subtitle: `Subscription renews every month, change or cancel any time.`,
    },
    payAsYouGoHeader: {
      title: `Pay as you go`,
      subtitle: `No commitment needed , buy classes when you need them. You're only charged when you attend.`,
    },
    plans: [
      {
        tag: `SUBSCRIPTION`,
        name: `Platinum`,
        price: `£120`,
        period: `per month`,
        isPopular: false,
        badge: `★ MOST VALUED`,
        buttonText: `UPGRADE`,
        metaTop: null,
        metaBottom: null,
        metaRight: { label: `Classes`, value: `Unlimited` },
        features: [
          `Unlimited Yogashala videos`,
          `Unlimited live online classes`,
          `Frequent member gifts`,
        ],
        theme: `platinum`
      },
      {
        tag: `ADD-ON`,
        name: `Teacher Subscription`,
        price: `Choose your teacher`,
        period: ``,
        isPopular: false,
        buttonText: `VIEW TEACHERS`,
        metaTop: null,
        metaBottom: null,
        metaRight: { label: `Teacher`, value: `Unlimited` },
        features: [
          `Unlimited access to all classes of your chosen teacher`,
          `Unlimited access to all videos of that teacher`,
          `Additional classes with other teachers via class pass`,
        ],
        theme: `gold`
      },
      {
        tag: `ADD-ON`,
        name: `Class pass pack`,
        price: `£24`,
        period: `per 4 classes`,
        originalPrice: `£31.96`,
        savings: `YOU SAVE £7.96 (25%)`,
        isPopular: false,
        buttonText: `CONTINUE`,
        metaTop: null,
        metaBottom: null,
        metaRight: null,
        features: [
          `Pack of 4 classes`,
          `Can be used for any class`,
          `Valid for 4 months`,
          `Class charged on participation`,
        ],
        theme: `silver`
      },
      {
        tag: `SUBSCRIPTION`,
        name: `Bronze`,
        price: `£7.99`,
        period: `per month`,
        isPopular: false,
        buttonText: `UPGRADE`,
        metaTop: null,
        metaBottom: null,
        metaRight: { label: `Access`, value: `Yogashala` },
        features: [
          `Unlimited Yogashala videos`,
          `Add-on class for £7.99`,
          `Free access to community classes`,
        ],
        theme: `bronze`
      },
      {
        tag: `ADD-ON`,
        name: `Class pass`,
        price: `£7.99`,
        period: `per class`,
        isPopular: false,
        buttonText: `CONTINUE`,
        metaTop: null,
        metaBottom: null,
        metaRight: null,
        features: [
          `Can be used for any class`,
          `Drop-in classes for £7.99`,
          `Upgrade any time`,
          `Class charged on participation`,
        ],
        theme: `silver`
      },
      {
        tag: `SUBSCRIPTION`,
        name: `Blue`,
        price: `Free`,
        period: `community access`,
        isPopular: false,
        buttonText: `UPGRADE`,
        metaTop: null,
        metaBottom: null,
        metaRight: null,
        features: [
          `Free access to community classes`,
          `Register with your credit card for membership`,
          `Drop-in classes available for £10`,
          `Upgrade any time`,
        ],
        theme: `blue`
      },
    ],
    footer: {
      policyTitle: `CANCELLATION POLICY`,
      policyDesc: `You can cancel your subscription any time. If you cancel, you'll continue to have access until the end of your current billing period — after that, no further charges will apply.
       Schedule your classes for free without prior charges.`,
      ctaTitle: `Ready to begin your <br/>*Tatva journey?*`,
      ctaDesc: `Thousands of practitioners have found their rhythm with Tatva. Your first class could be the one that changes everything — start today.`,
      ctaButton: `EXPLORE ALL PLANS ON TATVA →`,
      ctaNote: `No commitment required · Cancel any time · Free plan always available`
    },
    heroButtons: {
      primary: `See All Plans`,
      secondary: `Try your first class free on Tatva →`,
    },
    welcomeOffer: {
      badge: ` A LITTLE SOMETHING TO BEGIN`,

      offers: [
        {
          iconType: `play`,
          iconColor: `pink`,
          title: `Your First Class Is On Us`,
          desc: `Take your first step without any pressure. Experience a premium Tatva class and discover what feels right for you.`,
        },

        {
          iconType: `shield`,
          iconColor: `bronze`,
          labelSmall: `START WITH 100% OFF`,
          title: `Bronze Plan`,
          code: `YOGYA100`,
        },

        {
          iconType: `shield`,
          iconColor: `platinum`,
          labelSmall: `GO DEEPER WITH 50% OFF`,
          title: `Platinum Plan`,
          code: `TATVA50`,
        },
      ],

      ctaText: `Your practice doesn't have to wait.\nTake the first step today.`,

      ctaButton: `Take your first Class →`,

      ctaNote: `Welcome offer available for 7 days`,
    },

    faq: {
      title: `Questions? *We've got you.*`,

      items: [
        {
          q: `Can I cancel whenever I need to?`,
          a: `Absolutely. Your practice should work around your life, not the other way around. Cancel anytime from your account settings, and you'll continue enjoying your membership until the end of your current billing period.`,
        },

        {
          q: `What happens after I cancel?`,
          a: `Nothing changes immediately. You'll keep full access to your membership until your current billing period ends. After that, you'll simply return to Tatva's free experience.`,
        },

        {
          q: `Will my membership renew automatically?`,
          a: `Yes. Your membership will renew automatically so you don't have to worry about losing access in the middle of your practice. We'll remind you before your next payment.`,
        },

        {
          q: `I'm completely new to yoga. Is Tatva for me?`,
          a: `Yes. You don't need to be flexible, experienced, or know where to start. Tatva has practices for complete beginners through advanced practitioners, with teachers who guide you at every step.`,
        },

        {
          q: `Can I change my plan later?`,
          a: `Of course. Your practice will evolve, and your membership can evolve with it. Upgrade or downgrade whenever you need, with changes taking effect from your next billing cycle.`,
        },

        {
          q: `Can I practice on my phone, tablet, and laptop?`,
          a: `Yes. Your Tatva membership travels with you. Sign in on your phone, tablet, or web browser and continue your practice wherever you feel most comfortable.`,
        },
      ],
    },

    cta: {
      title: `Your practice starts with <br/>*one small step.*`,

      desc: `You don't need to change your whole life to make room for yoga. Start with one class, find what feels good, and let the practice grow from there. Tatva is here whenever you're ready.`,

      primaryButton: `Find My Plan →`,

      secondaryButton: `Try a Free Class`,

      benefits: [
        `Start without pressure`,
        `Cancel whenever you need`,
        `Free classes always available`,
      ],

      image: `/homepage/hero-image.webp`,

      imageAlt: `A peaceful moment in the Tatva yoga journey`,
    },
  },
  privacypage: {
    "logo": {
      "src": "/logo.svg"
    },
    "sections": ["section1", "section2", "section3", "section4", "section5", "section6", "section7", "section8", "section9", "section10"],
    "section1": {
      "title": {
        "text": "Privacy Policy"
      },
      "content": ["Tatva Wellness Ltd. built the Tatva app as a Commercial app. This SERVICE is provided by Tatva Wellness Ltd. and is intended for use as it is.", "This page is used to inform visitors regarding our policies with the collection, use, and disclosure of Personal Information if anyone decided to use our Service.", "If you choose to use our Service, then you agree to the collection and use of information in relation to this policy. The Personal Information that we collect is used for providing and improving the Service. We will not use or share your information with anyone except as described in this Privacy Policy.", "        The terms used in this Privacy Policy have the same meanings as in our Terms and Conditions, which are accessible at Tatva unless otherwise defined in this Privacy Policy."]
    },
    "section2": {
      "title": {
        "text": "Information Collection and Use"
      },
      "content": ["For a better experience, while using our Service, we may require you to provide us with certain personally identifiable information. The information that we request will be retained by us and used as described in this privacy policy.", "The app does use third-party services that may collect information used to identify you.", "<ul>Link to the privacy policy of third-party service providers used by the app", "<li><a href='https://www.google.com/policies/privacy/' target='_blank' rel='noopener noreferrer'> Google Play Services </a></li><li><a href='https://firebase.google.com/support/privacy/' target='_blank' rel='noopener noreferrer'>Firebase Crashlytics</a></li></ul>"]
    },
    "section3": {
      "title": {
        "text": "Log Data"
      },
      "content": [" We want to inform you that whenever you use our Service, in a case of an error in the app we collect data and information (through third-party products) on your phone called Log Data. This Log Data may include information such as your device Internet Protocol (“IP”) address, device name, operating system version, the configuration of the app when utilizing our Service, the time and date of your use of the Service, and other statistics."]
    },
    "section4": {
      "title": {
        "text": "Cookies"
      },
      "content": ["Cookies are files with a small amount of data that are commonly used as anonymous unique identifiers. These are sent to your browser from the websites that you visit and are stored on your device internal memory.", "This Service does not use these “cookies” explicitly. However, the app may use third-party code and libraries that use “cookies” to collect information and improve their services. You have the option to either accept or refuse these cookies and know when a cookie is being sent to your device. If you choose to refuse our cookies, you may not be able to use some portions of this Service."]
    },
    "section5": {
      "title": {
        "text": "Service Providers"
      },
      "content": ["<ul>We may employ third-party companies and individuals due to the following reasons:", "<li>To facilitate our Service;</li><li>To provide the Service on our behalf;</li><li>To perform Service-related services; or</li><li>To assist us in analyzing how our Service is used.</li></ul>", "We want to inform users of this Service that these third parties have access to their Personal Information. The reason is to perform the tasks assigned to them on our behalf. However, they are obligated not to disclose or use the information for any other purpose."]
    },
    "section6": {
      "title": {
        "text": "Security"
      },
      "content": ["We value your trust in providing us your Personal Information, thus we are striving to use commercially acceptable means of protecting it. But remember that no method of transmission over the internet, or method of electronic storage is 100% secure and reliable, and we cannot guarantee its absolute security."]
    },
    "section7": {
      "title": {
        "text": "Links to Other Sites"
      },
      "content": ["This Service may contain links to other sites. If you click on a third-party link, you will be directed to that site. Note that these external sites are not operated by us. Therefore, we strongly advise you to review the Privacy Policy of these websites. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services."]
    },
    "section8": {
      "title": {
        "text": "Children Privacy"
      },
      "content": ["These Services do not address anyone under the age of 13. We do not knowingly collect personally identifiable information from children under 13 years of age. In the case we discover that a child under 13 has provided us with personal information, we immediately delete this from our servers. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us so that we will be able to do the necessary actions."]
    },
    "section9": {
      "title": {
        "text": "Changes to This Privacy Policy"
      },
      "content": ["We may update our Privacy Policy from time to time. Thus, you are advised to review this page periodically for any changes. We will notify you of any changes by posting the new Privacy Policy on this page.", "This policy is effective as of 2023-06-21"]
    },
    "section10": {
      "title": {
        "text": "Contact Us"
      },
      "content": ["If you have any questions or suggestions about our Privacy Policy, do not hesitate to contsact us at support@tatva.app."]
    }
  },
  teacherspage: {
    hero: {
      tag: "OUR TEACHERS",
      title: "Learn from those who <br/> *live the practice.*",
      subtitle: "Every Tatva teacher carries years of lived experience, deep lineage, and a genuine calling to guide. Find the one whose path speaks to yours.",
      buttons: {
        primary: "Share your passion, Teach on Tatva",
      },
    },
    teachers: [
      {
        name: "Yogi Ashokananda",
        theme: "darkGreen"
      },
      {
        name: "Francesca",
        theme: "lightGreen"
      },
      {
        name: "Katarina Kamara",
        theme: "darkGreen"
      },
      {
        name: "Carlo Mancini",
        theme: "darkGreen"
      },
      {
        name: "Kerry",
        theme: "darkGreen"
      },
      {
        name: "Lesley",
        theme: "silver"
      }
    ],
    testimonial: {
      quote: `"A teacher who lives the practice changes something in you that no tutorial ever could."`,
      author: `— TATVA COMMUNITY`
    },
    footer: {
      title: `Find your *perfect guide.*`,
      subtitle: `Browse all 40+ teachers on Tatva, filter by style, language, or price, and start your first class today.`,
      buttonText: `EXPLORE ALL TEACHERS →`,
      note: `Free plan available · No lock-in · Cancel any time`
    },
    teacherApproachBanner: {
      badge: "🧘 TEACH ON TATVA",
      title: "Are you a Yoga Teacher? *Join the Tatva Platform.*",
      statement: "Share your wisdom with a dedicated global community. Whether you guide live online sessions, manage student batches, or build on-demand video courses.",
      highlights: [
        "Make your Home your studio",
        "Automated scheduling & Zoom sync",
        "Reach global students effortlessly"
      ],
      buttonText: "Create Your Teacher Account →",
      loginText: "Already a Tatva teacher?",
      loginLinkText: "Log in to Teachers Webapp",
      note: "Free to set up · No credit card required · Start teaching live in minutes"
    },
    webappOverview: {
      title: "You Teach. *We'll Handle The Rest.*",
      subtitle: "With Tatva, manage your classes, connect with students, and get paid seamlessly—all while we take care of the busywork so you can focus on teaching.",
      cta: {
        buttonText: "Create your teacher account",
        loginText: "Already have an account?",
        loginLinkText: "Log in to the teachers webapp",
        note: "Free to set up · Bring your existing batches in a spreadsheet · Your first class could be live today"
      },
      features: [
        {
          icon: "FiCalendar",
          title: "Plan & Manage with Ease",
          description: "Create your class schedule in minutes and let students book instantly. No more back-and-forth.",
          bullets: [
            "Smart scheduling & calendar sync",
            "Instant class bookings",
            "Automated reminders"
          ]
        },
        {
          icon: "FiTag",
          title: "Set Your Price, Your Way",
          description: "Choose your subscription plans, offer discounts, and get paid securely – all automated.",
          bullets: [
            "Custom subscription plans",
            "Create coupons & offers",
            "Automated, secure payouts"
          ]
        },
        {
          icon: "FiUsers",
          title: "Build & Engage Your Community",
          description: "Connect with your students, host live sessions, and build a community that grows with you.",
          bullets: [
            "Live class management",
            "Student groups & communication",
            "Track attendance & engagement"
          ]
        },
        {
          icon: "FiVideo",
          title: "Share Your Knowledge",
          description: "Upload your pre-recorded classes and guides so students can learn anytime, anywhere.",
          bullets: [
            "Upload unlimited videos",
            "Organize content your way",
            "Earn from on-demand classes"
          ]
        },
        {
          icon: "FiGlobe",
          title: "Reach More. Impact More.",
          description: "Go beyond borders and share your authentic teachings with students around the world.",
          bullets: [
            "Global visibility for your profile",
            "Teach across time zones",
            "Grow your student base"
          ]
        },
        {
          icon: "FiTrendingUp",
          title: "Understand & Grow",
          description: "Get real insights into your classes, earnings, and students – so you can grow with confidence.",
          bullets: [
            "Earnings & performance dashboard",
            "Student insights & feedback",
            "Data to improve & scale"
          ]
        }
      ]
    }
  },
  teachOnTatvaPage: {
    benefits: {
      title: "You Bring the Practice.<br/>We Bring *the World.*",
      ctaText: "Teach on Tatva",
      cards: [
        {
          icon: "FiGlobe",
          title: "Build a Community Beyond Your Geography",
          heading: "Reach students beyond the boundaries of your location.",
          bullets: [
            "Teach beyond the limits of your city, studio, or geography",
            "Connect with practitioners who resonate with your teaching",
            "Build belonging, connection, and shared growth"
          ]
        },
        {
          icon: "FiHome",
          title: "Make Your Home Your Studio with Tatva",
          heading: "Teach from where you feel most comfortable.",
          bullets: [
            "Reach students anywhere, right from your home",
            "Your location no longer defines your teaching space",
            "Create meaningful experiences without a traditional studio"
          ]
        },
        {
          icon: "FiVideo",
          title: "Teach Live. Inspire Beyond the Moment.",
          heading: "Bring your energy to live classes and your knowledge to on-demand learning.",
          bullets: [
            "Connect through interactive live sessions",
            "Share your expertise through on-demand content",
            "Keep inspiring and creating value after the class ends"
          ]
        },
        {
          icon: "FiTrendingUp",
          title: "Turn Your Expertise into Sustainable Income",
          heading: "Your knowledge deserves to grow with you.",
          bullets: [
            "Offer individual classes, multi-class passes, and subscriptions",
            "Create multiple income opportunities",
            "Build recurring relationships with your students"
          ]
        },
        {
          icon: "FiHeart",
          title: "Create Deeper Student Connections",
          heading: "Go beyond teaching a class—become part of their journey.",
          bullets: [
            "Consistent live sessions and accessible content",
            "Turn one-time students into long-term practitioners",
            "Build trust, motivation, and loyalty with every interaction"
          ]
        },
        {
          icon: "FiHeadphones",
          title: "We're With You, 24/7",
          heading: "You focus on teaching. Tatva's support team is here when you need us.",
          bullets: [
            "Get 24/7 support whenever you need assistance",
            "Technical and platform concerns handled for you",
            "Stay focused on your students and your teaching"
          ]
        }
      ]
    },
    steps: {
      title: "Start teaching with<br/>Tatva in three steps.",
      items: [
        {
          tone: "primary",
          badge: "About 10 mins",
          title: "Submit your application",
          description: "Share your bio, certifications (200h / 500h RYT) and insurance policy details."
        },
        {
          tone: "outline",
          badge: "24–48 hours",
          title: "Get Approved",
          description: "Our team reviews your credentials, studio setup, and other details"
        },
        {
          tone: "dark",
          badge: "Go live",
          title: "Set up & start teaching",
          description: "Connect Zoom and your bank in one click each, publish your classes, and welcome students from around the world."
        }
      ]
    },
    applyCta: {
      title: "Ready to focus 100% on<br/>*teaching?*",
      subtitle: "Apply in minutes, to start your teaching journey with Tatva",
      buttonText: "Apply to Teach"
    }
  }
};
